/**
 * Product Picker - Fast product navigation with instant page transitions
 *
 * Features:
 * - Horizontal thumbnail row with native CSS scroll
 * - View Transitions API for smooth page changes
 * - Hover prefetching for instant navigation
 * - AJAX content replacement fallback
 */

if (!customElements.get('product-picker')) {
  customElements.define(
    'product-picker',
    class ProductPicker extends HTMLElement {
      constructor() {
        super();
        this.track = null;
        this.currentProductHandle = this.dataset.currentProduct;
        this.prefetchedUrls = new Set();
        this.prefetchTimeout = null;
        this.isNavigating = false;
      }

      connectedCallback() {
        this.track = this.querySelector('.product-picker__track');
        this.attachEventListeners();
        this.setupPrefetching();
        this.setupNavButtons();
        this.scrollToCurrentProduct();

        // Handle browser back/forward
        window.addEventListener('popstate', this.handlePopState.bind(this));
      }

      disconnectedCallback() {
        window.removeEventListener('popstate', this.handlePopState.bind(this));
      }

      /**
       * Setup navigation button click handlers
       */
      setupNavButtons() {
        const prevBtn = this.querySelector('[data-scroll-prev]');
        const nextBtn = this.querySelector('[data-scroll-next]');
        const scrollAmount = 344; // ~4 items (80px + 6px gap) * 4

        if (prevBtn) {
          prevBtn.addEventListener('click', () => {
            this.track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
          });
        }

        if (nextBtn) {
          nextBtn.addEventListener('click', () => {
            this.track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
          });
        }

        // Update button states on scroll
        if (this.track) {
          this.track.addEventListener('scroll', () => this.updateNavButtons());
          this.updateNavButtons();
        }
      }

      /**
       * Update navigation button disabled states
       */
      updateNavButtons() {
        const prevBtn = this.querySelector('[data-scroll-prev]');
        const nextBtn = this.querySelector('[data-scroll-next]');

        if (prevBtn) {
          prevBtn.disabled = this.track.scrollLeft <= 0;
        }

        if (nextBtn) {
          const maxScroll = this.track.scrollWidth - this.track.clientWidth;
          nextBtn.disabled = this.track.scrollLeft >= maxScroll - 1;
        }
      }

      /**
       * Attach click handlers with navigation interception
       */
      attachEventListeners() {
        this.addEventListener('click', this.handleClick.bind(this));
      }

      /**
       * Handle product thumbnail click
       *
       * On laisse la navigation native du lien <a> se faire (rechargement de page)
       * au lieu d'un swap AJAX de .main-product : le swap remplaçait l'innerHTML et
       * cassait les apps montées une seule fois au chargement (wishlist Flits, widget
       * abonnement Appstle, avis...), qui ne se re-montaient jamais sur le nouveau DOM.
       * Le prefetch au survol (setupPrefetching) garde une navigation rapide.
       */
      handleClick(event) {
        const item = event.target.closest('.product-picker__item');
        if (!item) return;

        // Bloquer uniquement le re-clic sur le produit déjà actif
        if (item.classList.contains('product-picker__item--active')) {
          event.preventDefault();
          return;
        }

        // Feedback visuel pendant le chargement de la nouvelle page
        item.classList.add('product-picker__item--loading');
        // Pas de preventDefault : le lien <a> navigue normalement.
      }

      /**
       * Navigate to product via AJAX content replacement
       */
      async navigateToProduct(url) {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Navigation failed');

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Get the new main product section
        const newMainProduct = doc.querySelector('.main-product');
        const currentMainProduct = document.querySelector('.main-product');

        if (newMainProduct && currentMainProduct) {
          // Replace content and re-inject scripts
          this.setInnerHTML(currentMainProduct, newMainProduct.innerHTML);
        }

        // Update browser history
        window.history.pushState({ productUrl: url }, '', url);

        // Update page title
        document.title = doc.title;

        // Re-initialize required JS components
        this.reinitializeComponents();

        // Scroll to top instantly
        window.scrollTo({ top: 0, behavior: 'instant' });

        // Dispatch event for other components
        document.dispatchEvent(
          new CustomEvent('productPickerNavigated', {
            detail: { url: window.location.href },
          })
        );
      }

      /**
       * Set innerHTML and re-inject scripts for execution
       */
      setInnerHTML(element, html) {
        element.innerHTML = html;

        // Re-inject scripts to allow execution
        element.querySelectorAll('script').forEach((oldScript) => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach((attr) => {
            newScript.setAttribute(attr.name, attr.value);
          });
          newScript.appendChild(document.createTextNode(oldScript.innerHTML));
          oldScript.parentNode.replaceChild(newScript, oldScript);
        });
      }

      /**
       * Re-initialize JavaScript components after content replacement
       */
      reinitializeComponents() {
        // Re-initialize Shopify Payment Button
        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        // Re-initialize 3D model viewer
        if (window.ProductModel) {
          window.ProductModel.loadShopifyXR();
        }
      }

      /**
       * Handle browser back/forward navigation
       */
      handlePopState(event) {
        if (event.state && event.state.productUrl) {
          this.navigateToProduct(event.state.productUrl);
        } else {
          // Reload the page for initial state
          window.location.reload();
        }
      }

      /**
       * Setup hover prefetching for fast navigation
       */
      setupPrefetching() {
        this.querySelectorAll('.product-picker__item').forEach((item) => {
          item.addEventListener('mouseenter', () => this.prefetchProduct(item));
          item.addEventListener('touchstart', () => this.prefetchProduct(item), { passive: true });
        });
      }

      /**
       * Prefetch a product page on hover
       */
      prefetchProduct(item) {
        const url = item.dataset.productUrl;
        if (this.prefetchedUrls.has(url)) return;

        // Debounce prefetch
        clearTimeout(this.prefetchTimeout);
        this.prefetchTimeout = setTimeout(() => {
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.href = url;
          link.as = 'document';
          document.head.appendChild(link);
          this.prefetchedUrls.add(url);
        }, 100);
      }

      /**
       * Scroll to show current product
       */
      scrollToCurrentProduct() {
        if (!this.track) return;

        const activeItem = this.querySelector('.product-picker__item--active');
        if (!activeItem) return;

        // Scroll to center the active item
        setTimeout(() => {
          const itemLeft = activeItem.offsetLeft;
          const itemWidth = activeItem.offsetWidth;
          const trackWidth = this.track.clientWidth;
          const scrollPos = itemLeft - (trackWidth / 2) + (itemWidth / 2);
          this.track.scrollLeft = Math.max(0, scrollPos);
          this.updateNavButtons();
        }, 50);
      }
    }
  );
}
